<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { useMotionSequence } from '@/composables/useMotionSequence'
import type { ArrivalBeat } from '@/lib/arrival/arrival-schedule'
import {
  BEAM_ERASE_INSET,
  BEAM_PAINT,
  bandSegments,
  beamEnvelope,
  beamHead,
  beamTrail,
  BLOOM_BANDS,
  CORE_CURVE,
  CORE_WIDTH,
  cornerShapeParameter,
  type BandSegment,
  type BeamPath,
  type EasingFunction,
  edgeGlowEnvelope,
  FADE_OUT_SHARE,
  FRINGE_ALPHA,
  FRINGE_OFFSET,
  FRINGE_WIDTH,
  GLOW_BLOOM_OFFSET,
  GLOW_BLOOM_WIDTH,
  GLOW_ERASE_INSET,
  GLOW_LINE_ALPHA,
  GLOW_LINE_WIDTH,
  GLOW_SEGMENTS,
  HEAD_RADIUS,
  MAX_PIXEL_RATIO,
  offsetPoints,
  parseCubicBezier,
  type PathPoint,
  type PathRect,
  roundedRectPath,
  TRAIL_CURVE,
  type TrailSample,
  verticalWeights,
} from './beam-path'

/** When the lap and the edge glow play, in milliseconds from the call to `play`. */
interface BeamTiming {
  readonly beam: ArrivalBeat
  readonly edgeGlow: ArrivalBeat
}

interface BeamColours {
  readonly core: string
  readonly halo: string
  readonly bloom: string
  readonly deep: string
}

interface BeamLayer {
  readonly canvas: HTMLCanvasElement
  readonly context: CanvasRenderingContext2D
}

interface Band {
  readonly segments: readonly BandSegment[]
  readonly colour: string
  readonly width: number
  readonly composite: GlobalCompositeOperation
}

interface BeamSession {
  readonly context: CanvasRenderingContext2D
  readonly scratch: BeamLayer
  readonly bloom: BeamLayer
  readonly width: number
  readonly height: number
  readonly ratio: number
  readonly rect: PathRect
  readonly radius: number
  readonly cornerShape: number
  readonly path: BeamPath
  readonly easing: EasingFunction
  readonly colours: BeamColours
  readonly blur: string
  readonly timing: BeamTiming
  readonly glowPoints: readonly PathPoint[]
  readonly glowWeights: readonly number[]
}

interface RunningBeam {
  frameId: number
  resolve: () => void
}

const LINEAR: EasingFunction = (progress) => progress

const canvas = ref<HTMLCanvasElement | null>(null)
const coreSwatch = ref<HTMLElement | null>(null)
const haloSwatch = ref<HTMLElement | null>(null)
const bloomSwatch = ref<HTMLElement | null>(null)
const deepSwatch = ref<HTMLElement | null>(null)

const props = defineProps<{
  /** The theme the beam is painted for: it picks the blending and the strength of every layer. */
  scheme: 'light' | 'dark'
}>()
const motion = useMotionSequence()

let running: RunningBeam | null = null

function swatchColour(swatch: HTMLElement | null): string {
  return swatch === null ? '' : getComputedStyle(swatch).color
}

function readColours(): BeamColours {
  return {
    core: swatchColour(coreSwatch.value),
    halo: swatchColour(haloSwatch.value),
    bloom: swatchColour(bloomSwatch.value),
    deep: swatchColour(deepSwatch.value),
  }
}

function createLayer(width: number, height: number, ratio: number): BeamLayer {
  const layer = document.createElement('canvas')
  layer.width = Math.max(1, Math.round(width * ratio))
  layer.height = Math.max(1, Math.round(height * ratio))
  const context = layer.getContext('2d')
  if (context === null) throw new Error('A 2D canvas is not available.')
  context.scale(ratio, ratio)
  return { canvas: layer, context }
}

function openSession(
  element: HTMLCanvasElement,
  target: HTMLElement,
  timing: BeamTiming,
): BeamSession | null {
  const context = element.getContext('2d')
  if (context === null) return null

  const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
  const canvasRect = element.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  element.width = Math.max(1, Math.round(canvasRect.width * ratio))
  element.height = Math.max(1, Math.round(canvasRect.height * ratio))

  const rect: PathRect = {
    x: targetRect.left - canvasRect.left,
    y: targetRect.top - canvasRect.top,
    width: targetRect.width,
    height: targetRect.height,
  }
  const style = getComputedStyle(target)
  const radius = parseFloat(style.borderTopLeftRadius) || 0
  const cornerShape = cornerShapeParameter(style.getPropertyValue('corner-shape'))
  const path = roundedRectPath(rect, radius, cornerShape)
  const glowPoints = Array.from({ length: GLOW_SEGMENTS + 1 }, (_, index) =>
    path.pointAt((index / GLOW_SEGMENTS) * path.perimeter),
  )

  return {
    context,
    scratch: createLayer(canvasRect.width, canvasRect.height, ratio),
    bloom: createLayer(canvasRect.width, canvasRect.height, ratio),
    width: canvasRect.width,
    height: canvasRect.height,
    ratio,
    rect,
    radius,
    cornerShape,
    path,
    easing: parseCubicBezier(motion.token('--ease-standard')) ?? LINEAR,
    colours: readColours(),
    blur: `blur(${parseFloat(motion.token('--spacing-2')) * ratio}px)`,
    timing,
    glowPoints,
    glowWeights: verticalWeights(glowPoints, rect),
  }
}

function clearLayer(session: BeamSession, layer: BeamLayer): void {
  layer.context.clearRect(0, 0, session.width, session.height)
}

function strokeBand(session: BeamSession, target: CanvasRenderingContext2D, band: Band): void {
  const { context } = session.scratch
  clearLayer(session, session.scratch)
  context.lineCap = 'butt'
  context.lineWidth = band.width
  context.strokeStyle = band.colour
  for (const segment of band.segments) {
    if (segment.alpha <= 0) continue
    context.globalAlpha = segment.alpha
    context.beginPath()
    context.moveTo(segment.fromX, segment.fromY)
    context.lineTo(segment.toX, segment.toY)
    context.stroke()
  }
  context.globalAlpha = 1

  target.save()
  target.globalCompositeOperation = band.composite
  target.drawImage(session.scratch.canvas, 0, 0, session.width, session.height)
  target.restore()
}

function spillBloom(session: BeamSession, blend: GlobalCompositeOperation): void {
  const { context } = session
  context.save()
  context.filter = session.blur
  context.globalCompositeOperation = blend
  context.drawImage(session.bloom.canvas, 0, 0, session.width, session.height)
  context.restore()
}

function eraseFace(session: BeamSession, inset: number): void {
  const { context, rect } = session
  context.save()
  context.globalCompositeOperation = 'destination-out'
  context.globalAlpha = 1
  context.fillStyle = session.colours.core
  const face = {
    x: rect.x + inset,
    y: rect.y + inset,
    width: rect.width - 2 * inset,
    height: rect.height - 2 * inset,
  }
  const outline = roundedRectPath(
    face,
    Math.max(0, session.radius - inset),
    session.cornerShape,
  ).outline()
  context.beginPath()
  outline.forEach((point, index) =>
    index === 0 ? context.moveTo(point.x, point.y) : context.lineTo(point.x, point.y),
  )
  context.closePath()
  context.fill()
  context.restore()
}

function trailIntensity(trail: readonly TrailSample[], curve: number, envelope: number) {
  return (index: number): number => {
    const position = (trail[index]!.position + trail[index + 1]!.position) / 2
    return position ** curve * envelope
  }
}

function paintGlowBloom(
  session: BeamSession,
  blend: GlobalCompositeOperation,
  scheme: 'light' | 'dark',
  envelope: number,
): void {
  const alpha = BEAM_PAINT[scheme].glowBloom * envelope
  const points = offsetPoints(session.glowPoints, GLOW_BLOOM_OFFSET)
  const { colours, glowWeights } = session
  clearLayer(session, session.bloom)
  const bloom = session.bloom.context
  strokeBand(session, bloom, {
    segments: bandSegments(points, (index) => alpha * (1 - glowWeights[index]!)),
    colour: colours.halo,
    width: GLOW_BLOOM_WIDTH,
    composite: blend,
  })
  strokeBand(session, bloom, {
    segments: bandSegments(points, (index) => alpha * glowWeights[index]!),
    colour: colours.deep,
    width: GLOW_BLOOM_WIDTH,
    composite: blend,
  })
  spillBloom(session, blend)
  eraseFace(session, GLOW_ERASE_INSET)
}

function paintGlowLine(session: BeamSession, envelope: number): void {
  const alpha = GLOW_LINE_ALPHA * envelope
  const { colours, glowPoints, glowWeights, context } = session
  strokeBand(session, context, {
    segments: bandSegments(glowPoints, (index) => alpha * (1 - glowWeights[index]!)),
    colour: colours.halo,
    width: GLOW_LINE_WIDTH,
    composite: 'source-over',
  })
  strokeBand(session, context, {
    segments: bandSegments(glowPoints, (index) => alpha * glowWeights[index]!),
    colour: colours.deep,
    width: GLOW_LINE_WIDTH,
    composite: 'source-over',
  })
}

function paintBeamBloom(
  session: BeamSession,
  blend: GlobalCompositeOperation,
  scheme: 'light' | 'dark',
  trail: readonly TrailSample[],
  envelope: number,
): void {
  const paint = BEAM_PAINT[scheme]
  const points = trail.map((sample) => sample.point)
  const intensity = trailIntensity(trail, TRAIL_CURVE, envelope)
  clearLayer(session, session.bloom)
  const bloom = session.bloom.context
  for (const band of BLOOM_BANDS) {
    strokeBand(session, bloom, {
      segments: bandSegments(
        offsetPoints(points, band.offset),
        (index) => paint[band.alpha] * intensity(index),
      ),
      colour: session.colours[band.colour],
      width: band.width,
      composite: blend,
    })
  }
  const head = points[points.length - 1]!
  bloom.save()
  bloom.globalCompositeOperation = blend
  bloom.globalAlpha = paint.head * envelope
  bloom.fillStyle = session.colours.halo
  bloom.beginPath()
  bloom.arc(head.x, head.y, HEAD_RADIUS, 0, Math.PI * 2)
  bloom.fill()
  bloom.restore()
  spillBloom(session, blend)
  eraseFace(session, BEAM_ERASE_INSET)
}

function paintBeamLines(
  session: BeamSession,
  blend: GlobalCompositeOperation,
  scheme: 'light' | 'dark',
  trail: readonly TrailSample[],
  envelope: number,
): void {
  const points = trail.map((sample) => sample.point)
  const fringe = trailIntensity(trail, TRAIL_CURVE, envelope)
  const core = trailIntensity(trail, CORE_CURVE, envelope)
  for (const offset of [FRINGE_OFFSET, -FRINGE_OFFSET]) {
    strokeBand(session, session.context, {
      segments: bandSegments(offsetPoints(points, offset), (index) => FRINGE_ALPHA * fringe(index)),
      colour: session.colours.halo,
      width: FRINGE_WIDTH,
      composite: blend,
    })
  }
  strokeBand(session, session.context, {
    segments: bandSegments(points, (index) => BEAM_PAINT[scheme].core * core(index)),
    colour: session.colours.core,
    width: CORE_WIDTH,
    composite: 'source-over',
  })
}

function paintFrame(session: BeamSession, elapsed: number, scheme: 'light' | 'dark'): void {
  const { context, timing } = session
  const blend: GlobalCompositeOperation = scheme === 'dark' ? 'lighter' : 'source-over'
  context.setTransform(session.ratio, 0, 0, session.ratio, 0, 0)
  context.clearRect(0, 0, session.width, session.height)

  const lap = (elapsed - timing.beam.delay) / timing.beam.duration
  const beamLight = lap < 0 ? 0 : beamEnvelope(lap)
  const glowLight = edgeGlowEnvelope((elapsed - timing.edgeGlow.delay) / timing.edgeGlow.duration)

  if (glowLight > 0) paintGlowBloom(session, blend, scheme, glowLight)

  const trail =
    beamLight > 0
      ? beamTrail(session.path, beamHead(session.easing, lap, session.path.perimeter))
      : []
  if (beamLight > 0) paintBeamBloom(session, blend, scheme, trail, beamLight)

  if (glowLight > 0) paintGlowLine(session, glowLight)
  if (beamLight > 0) paintBeamLines(session, blend, scheme, trail, beamLight)
}

function totalMs(timing: BeamTiming): number {
  const beamEnd = timing.beam.delay + timing.beam.duration * (1 + FADE_OUT_SHARE)
  return Math.max(beamEnd, timing.edgeGlow.delay + timing.edgeGlow.duration)
}

function stop(): void {
  if (running === null) return
  cancelAnimationFrame(running.frameId)
  const { resolve } = running
  running = null
  const element = canvas.value
  element?.getContext('2d')?.clearRect(0, 0, element.width, element.height)
  resolve()
}

/** Runs one lap of light round `target`'s edge and the edge glow after it; resolves once the glow has gone. Resolves at once under reduced motion. */
function play(target: HTMLElement, timing: BeamTiming): Promise<void> {
  const element = canvas.value
  if (motion.reduced.value || element === null) return Promise.resolve()
  stop()
  const session = openSession(element, target, timing)
  if (session === null) return Promise.resolve()

  return new Promise<void>((resolve) => {
    const startedAt = performance.now()
    const duration = totalMs(timing)
    const current: RunningBeam = { frameId: 0, resolve }
    running = current

    const frame = (now: number): void => {
      const elapsed = now - startedAt
      if (elapsed >= duration) {
        stop()
        return
      }
      paintFrame(session, elapsed, props.scheme)
      current.frameId = requestAnimationFrame(frame)
    }
    current.frameId = requestAnimationFrame(frame)
  })
}

onBeforeUnmount(stop)

defineExpose({ play })
</script>

<template>
  <div class="pointer-events-none absolute inset-0" aria-hidden="true">
    <canvas ref="canvas" class="block size-full" />
    <div class="hidden">
      <span ref="coreSwatch" class="text-beam-core" />
      <span ref="haloSwatch" class="text-beam-halo" />
      <span ref="bloomSwatch" class="text-accent-blue" />
      <span ref="deepSwatch" class="text-accent-blue-active" />
    </div>
  </div>
</template>
